import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Toaster, toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, User, Mail, Lock, Shield } from 'lucide-react';

const inputBase =
  'w-full bg-slate-900/60 border border-slate-700 text-white placeholder-slate-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all duration-200';

export default function RegularUserRegistration({ registerBtn }) {
  const [formValues, setFormValues] = useState({
    role: 4,
    userEmail: '',
    userPassword: '',
    userConfPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const server_url = process.env.REACT_APP_API_URI;

  const updateFormValue = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleRegisterFormSubmit = async (e) => {
    e.preventDefault();
    const { userPassword, userConfPassword } = formValues;

    if (userPassword !== userConfPassword) {
      toast.error("Passwords don't match. Please try again.");
      return;
    }

    const newUser = { ...formValues };
    const referralId = localStorage.getItem('referralId');
    if (referralId) newUser.referredId = referralId;

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${server_url}/user/register`,
        newUser,
        { headers: { 'Content-Type': 'application/json' } },
      );

      toast.success(
        response.data.message ||
          'Registration successful! Redirecting to login...',
      );

      if (referralId) localStorage.removeItem('referralId');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          'Registration failed. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] py-12 bg-primary font-montserrat px-4">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="w-full bg-[#111827] shadow-2xl rounded-3xl overflow-hidden border border-slate-800">
            {/* Card Header */}
            <div className="bg-gradient-to-br from-[#081325] to-[#112140] p-8 text-white relative flex flex-col items-center">
              <button
                type="button"
                onClick={registerBtn}
                className="absolute left-6 top-6 flex items-center text-sm gap-1 text-white/70 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>

              <div className="w-16 h-16 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-4 mt-4">
                <User strokeWidth={1.5} className="w-8 h-8 text-blue-400" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-center tracking-tight">
                Consumer Registration
              </h1>
              <p className="mt-2 text-blue-200/80 text-center text-sm max-w-xs">
                Create your free account to start reviewing and rating.
              </p>
            </div>

            {/* Card Body */}
            <div className="p-8">
              <form onSubmit={handleRegisterFormSubmit} className="space-y-5">
                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    required
                    type="email"
                    name="userEmail"
                    placeholder="you@example.com"
                    value={formValues.userEmail}
                    onChange={updateFormValue}
                    autoComplete="email"
                    className={inputBase}
                  />
                  <p className="text-xs text-slate-500">
                    We'll never share your email with anyone else.
                  </p>
                </div>

                {/* Password Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Lock className="w-4 h-4 text-slate-400" />
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        required
                        type={showPassword ? 'text' : 'password'}
                        name="userPassword"
                        placeholder="Password"
                        value={formValues.userPassword}
                        onChange={updateFormValue}
                        autoComplete="new-password"
                        className={`${inputBase} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showPassword ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-slate-300">
                      Confirm <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        required
                        type={showConfPassword ? 'text' : 'password'}
                        name="userConfPassword"
                        placeholder="Confirm"
                        value={formValues.userConfPassword}
                        onChange={updateFormValue}
                        autoComplete="new-password"
                        className={`${inputBase} pr-10`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfPassword((p) => !p)}
                        className="absolute right-3 top-3.5 text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        {showConfPassword ? (
                          <Eye className="w-4 h-4" />
                        ) : (
                          <EyeOff className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    required
                    className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-500 accent-blue-500 focus:ring-blue-500 cursor-pointer shrink-0"
                  />
                  <span className="text-xs text-slate-400 group-hover:text-slate-300 transition-colors leading-relaxed">
                    I accept the{' '}
                    <span className="text-blue-400 font-medium">
                      Terms of Use
                    </span>{' '}
                    and{' '}
                    <span className="text-blue-400 font-medium">
                      Privacy Policy
                    </span>
                  </span>
                </label>

                {/* Privacy notice */}
                <div className="flex items-start gap-3 bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                  <Shield className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Your data is kept private and never sold to third parties.
                    You can delete your account at any time from your profile
                    settings.
                  </p>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 rounded-xl text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all font-semibold py-3 shadow-lg shadow-blue-500/20 text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v8z"
                        />
                      </svg>
                      Registering...
                    </>
                  ) : (
                    <>
                      <User className="w-4 h-4" />
                      Create Account
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Footer link */}
          <p className="text-center text-sm text-slate-500 mt-6">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
            >
              Log in
            </button>
          </p>
        </div>
      </div>
    </>
  );
}
